import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import httpClient from "../../utils/httpClients";

function Songs() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [artistIds, setArtistIds] = useState([]);

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;

  const [formData, setFormData] = useState({
    title: "",
    artist: "",
    releaseDate: "",
    coverImage: null,
    audioFile: null,
    description: "",
    themes: "",
    alternativeTitle: "",
    recordingVersionTitle: "",
    recordingRadioProducer: "",
    catalogNumber: "",
    LabelName: "",
    writers: "",
    writersIPICode: "",
    iswc: "",
    mlcSongCode: "",
    songPublisher: "",
    upcEan: "",
    isrc: "",
  });

  const [editFormData, setEditFormData] = useState({
    title: "",
    artist: "",
    releaseDate: "",
    coverImage: null,
    audioFile: null,
    description: "",
    themes: "",
    alternativeTitle: "",
    recordingVersionTitle: "",
    recordingRadioProducer: "",
    catalogNumber: "",
    LabelName: "",
    writers: "",
    writersIPICode: "",
    iswc: "",
    mlcSongCode: "",
    songPublisher: "",
    upcEan: "",
    isrc: "",
  });

  // File preview states
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [audioFileName, setAudioFileName] = useState("");
  const [editCoverImagePreview, setEditCoverImagePreview] = useState(null);
  const [editAudioFileName, setEditAudioFileName] = useState("");
  const [themes, setThemes] = useState([]);

  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isValidImageFile = (file) => {
    if (!file) return false;
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    const type = (file.type || "").toLowerCase();
    return (
      ["image/jpeg", "image/png", "image/jpg"].includes(type) ||
      ["jpg", "jpeg", "png"].includes(ext)
    );
  };
  const isValidAudioFile = (file) => {
    if (!file) return false;
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    const type = (file.type || "").toLowerCase();
    return (
      ["audio/mpeg", "audio/wav", "audio/mp3", "audio/x-wav"].includes(type) ||
      ["mp3", "wav"].includes(ext)
    );
  };

  // Fetch songs from API
  const fetchSongs = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoadingSongs(true);
        const response = await httpClient.get(
          `admin/media-track/artist-get-all?contentType=song&search=${search}&page=${page}&limit=${pageSize}`,
        );

        if (response.data.success) {
          const songsData = response.data.data;
          setSongs(songsData.records || []);
          setTotalPages(songsData.totalPages || 1);
          setTotalRecords(songsData.totalRecords || 0);
          setCurrentPage(songsData.currentPage || 1);
        }
      } catch (error) {
        console.error("Error fetching songs:", error);
        // toast.error("Failed to load songs");
        setSongs([]);
      } finally {
        setLoadingSongs(false);
      }
    },
    [pageSize],
  );

  const fetchThemes = async () => {
    try {
      const res = await httpClient.get(`/user/music/get-themes`);

      if (res.data?.success) {
        const data = res.data.data.getThemes || [];
        setThemes(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchThemes();
    fetchSongs();
  }, []);

  useEffect(() => {
    getArtistIdS();
  }, []);

  const getArtistIdS = async () => {
    console.log("Function called-- ");
    try {
      const response = await httpClient.get(`artist/misc/get-artist`);
      console.log("Raw Response:", response);
      const artistIds = response.data?.data.artist?._id;
      console.log("artistIds", artistIds);
      setArtistIds(artistIds);

      if (response.data.success) {
        // console.log("Artist get data -", data);
      } else {
        console.log("Data success false hai:", response.data.message);
      }
    } catch (error) {
      console.error("Catch Block Error:", error);
    }
  };

  // Search handler with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSongs(1, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchSongs]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "audioFile" || name === "coverImage") {
      const file = files[0];

      if (file) {
        const isImage = name === "coverImage" && isValidImageFile(file);
        const isAudio = name === "audioFile" && isValidAudioFile(file);
        const typeError =
          name === "coverImage"
            ? isImage
              ? ""
              : "Please choose an image file (JPEG or PNG)."
            : isAudio
              ? ""
              : "Please choose an audio file (MP3 or WAV).";
        setFormErrors((prev) => ({ ...prev, [name]: typeError }));
        if (typeError) return;
        setFormData((prev) => ({
          ...prev,
          [name]: file,
        }));
        if (name === "coverImage") {
          const reader = new FileReader();
          reader.onload = (e) => setCoverImagePreview(e.target.result);
          reader.readAsDataURL(file);
        } else {
          setAudioFileName(file.name);
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: null }));
        if (name === "coverImage") setCoverImagePreview(null);
        else setAudioFileName("");
        setFormErrors((prev) => ({
          ...prev,
          [name]:
            name === "coverImage"
              ? "Cover image is required."
              : "Audio file is required.",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing (non-file fields)
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "audioFile" || name === "coverImage") {
      const file = files[0];

      if (file) {
        setEditFormData((prev) => ({
          ...prev,
          [name]: file,
        }));

        // Show file preview/name
        if (name === "coverImage") {
          const reader = new FileReader();
          reader.onload = (e) => {
            setEditCoverImagePreview(e.target.result);
          };
          reader.readAsDataURL(file);
        } else if (name === "audioFile") {
          setEditAudioFileName(file.name);
        }
      }
    } else {
      setEditFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title?.trim()) errors.title = "Song name is required.";
    if (!formData.title.length >100) errors.title = "Song name should be maximum 100 characters is required.";

    if (!formData.releaseDate) errors.releaseDate = "Release date is required.";
    if (!formData.themes) errors.themes = "Please select a theme.";
    if (!formData.coverImage) errors.coverImage = "Cover image is required.";
    if (!formData.audioFile) errors.audioFile = "Audio file is required.";
    if (!formData.description) errors.description = "Description is required.";
    if (!formData.recordingVersionTitle)
      errors.recordingVersionTitle = "Recording Version title is required.";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return false;
    }

    setFormErrors({});
    return true;
  };

  const resetForm = () => {
    setFormData({
      title: "",
      artist: "",
      releaseDate: "",
      coverImage: null,
      audioFile: null,
      description: "",
      themes: "",
      alternativeTitle: "",
      recordingVersionTitle: "",
      recordingRadioProducer: "",
      catalogNumber: "",
      LabelName: "",
      writers: "",
      writersIPICode: "",
      iswc: "",
      mlcSongCode: "",
      songPublisher: "",
      upcEan: "",
      isrc: "",
    });
    setCoverImagePreview(null);
    setAudioFileName("");
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setUploading(true);

    try {
      const uploadData = new FormData();
      uploadData.append("contentType", "song");
      uploadData.append("title", formData.title);
      uploadData.append("isAlbum", "false");
      // uploadData.append("artist", formData.artist);
      uploadData.append("description", formData.description);
      uploadData.append("releaseDate", formData.releaseDate);
      uploadData.append("isActive", "true");
      uploadData.append("isFeatured", "false");
      uploadData.append("artistIds", artistIds);

      // Append new fields
      uploadData.append("alternativeTitle", formData.alternativeTitle);
      uploadData.append(
        "recordingVersionTitle",
        formData.recordingVersionTitle,
      );
      uploadData.append(
        "recordingRadioProducer",
        formData.recordingRadioProducer,
      );
      uploadData.append("catalogNumber", formData.catalogNumber);
      uploadData.append("LabelName", formData.LabelName);
      uploadData.append("writers", formData.writers);
      uploadData.append("writersIPICode", formData.writersIPICode);
      uploadData.append("iswc", formData.iswc);
      uploadData.append("mlcSongCode", formData.mlcSongCode);
      uploadData.append("songPublisher", formData.songPublisher);
      uploadData.append("upcEan", formData.upcEan);
      uploadData.append("isrc", formData.isrc);

      // Append theme ID (not name)
      if (formData.themes) {
        uploadData.append("themes", formData.themes);
      }

      if (formData.coverImage) {
        uploadData.append("coverImage", formData.coverImage);
      }
      if (formData.audioFile) {
        uploadData.append("file", formData.audioFile);
      }

      await httpClient.post("admin/media-track/add-track", uploadData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // toast.success("Song uploaded successfully");
      setIsModalOpen(false);
      resetForm();
      await fetchSongs(currentPage, searchTerm);
    } catch (error) {
      console.error("Error uploading song:", error);
      // toast.error("Failed to upload song");
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const editErrors = {};
      if (!editFormData.title?.trim())
        editErrors.title = "Song title is required";
      if (!editFormData.title?.length>100)
        editErrors.title = "Song title should be maximum 100 characters is required";
    if (!editFormData.releaseDate)
      editErrors.releaseDate = "Release date is required";
    if (!editFormData.themes) editErrors.themes = "Theme is required";
    if (!editFormData.description?.trim())
      editErrors.description = "Description is required";
    if (!editFormData.recordingVersionTitle)
      editErrors.recordingVersionTitle = "Recording version  title is required";

    if (editFormData.coverImage && !isValidImageFile(editFormData.coverImage)) {
      editErrors.coverImage = "Please choose an image file (JPEG or PNG).";
    }
    if (editFormData.audioFile && !isValidAudioFile(editFormData.audioFile)) {
      editErrors.audioFile = "Please choose an audio file (MP3 or WAV).";
    }

    if (Object.keys(editErrors).length > 0) {
      setEditFormErrors(editErrors);
      return;
    }

    setEditFormErrors({});

    try {
      const updateData = new FormData();
      updateData.append("title", editFormData.title);
      // updateData.append("artist", editFormData.artist);
      updateData.append("description", editFormData.description);
      updateData.append("releaseDate", editFormData.releaseDate);
      updateData.append("artistIds", artistIds);
      updateData.append("isActive", "true");

      // Append new fields
      updateData.append("alternativeTitle", editFormData.alternativeTitle);
      updateData.append(
        "recordingVersionTitle",
        editFormData.recordingVersionTitle,
      );
      updateData.append(
        "recordingRadioProducer",
        editFormData.recordingRadioProducer,
      );
      updateData.append("catalogNumber", editFormData.catalogNumber);
      updateData.append("LabelName", editFormData.LabelName);
      updateData.append("writers", editFormData.writers);
      updateData.append("writersIPICode", editFormData.writersIPICode);
      updateData.append("iswc", editFormData.iswc);
      updateData.append("mlcSongCode", editFormData.mlcSongCode);
      updateData.append("songPublisher", editFormData.songPublisher);
      updateData.append("upcEan", editFormData.upcEan);
      updateData.append("isrc", editFormData.isrc);

      // Append theme ID (not name)
      if (editFormData.themes) {
        updateData.append("themes", editFormData.themes);
      }

      // Append files only if they are selected
      if (editFormData.coverImage) {
        updateData.append("coverImage", editFormData.coverImage);
      }
      if (editFormData.audioFile) {
        updateData.append("file", editFormData.audioFile);
      }

      await httpClient.put(
        `admin/media-track/update/${editingSong._id}`,
        updateData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      // toast.success("Song updated successfully");
      setIsEditModalOpen(false);
      setEditingSong(null);
      await fetchSongs(currentPage, searchTerm);
    } catch (error) {
      console.error("Error updating song:", error);
      // toast.error("Failed to update song");
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  const handleDeleteSong = async (songId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await httpClient.delete(`admin/media-track/delete/${songId}`);
        setSongs((prev) => prev.filter((song) => song._id !== songId));
        setTotalRecords((prev) => prev - 1);
      } catch (error) {
        console.error("Error deleting song:", error);
      }
    }
  };

  const handleApprove = async (songId) => {
    try {
      await httpClient.post(`admin/songs/approve/${songId}`);
      setSongs(
        songs.map((s) =>
          s._id === songId ? { ...s, isApproved: "approved" } : s,
        ),
      );
      // toast.success("Song approved successfully");
    } catch (error) {
      console.error("Error approving song:", error);
      // toast.error("Failed to approve song");
    }
  };

  const handleDeny = async (songId) => {
    try {
      await httpClient.post(`admin/songs/deny/${songId}`);
      setSongs(songs.filter((s) => s._id !== songId));
      // toast.success("Song denied successfully");
    } catch (error) {
      console.error("Error denying song:", error);
      // toast.error("Failed to deny song");
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const openEditModal = (song) => {
    setEditingSong(song);
    const themesValue = Array.isArray(song.themes)
      ? (song.themes[0]?._id ?? song.themes[0] ?? "")
      : song.themes || "";
    const writersValue = Array.isArray(song.writers)
      ? song.writers.filter(Boolean).join(", ") || (song.writers[0] ?? "")
      : song.writers || "";
    const writersIPIValue = Array.isArray(song.writersIPICode)
      ? song.writersIPICode.filter(Boolean).join(", ") ||
        (song.writersIPICode[0] ?? "")
      : song.writersIPICode || "";
    setEditFormData({
      title: song.title || "",
      artist: song.artist || "",
      releaseDate: song.releaseDate ? song.releaseDate.split("T")[0] : "",
      description: song.description || "",
      themes: themesValue,
      alternativeTitle: song.alternativeTitle || "",
      recordingVersionTitle: song.recordingVersionTitle || "",
      recordingRadioProducer: song.recordingRadioProducer || "",
      catalogNumber: song.catalogNumber || "",
      LabelName: song.LabelName || "",
      writers: writersValue,
      writersIPICode: writersIPIValue,
      iswc: song.iswc || "",
      mlcSongCode: song.mlcSongCode || "",
      songPublisher: song.songPublisher || "",
      upcEan: song.upcEan || "",
      isrc: song.isrc || "",
      coverImage: null,
      audioFile: null,
    });
    setEditCoverImagePreview(song.coverImageUrl || null);
    setEditAudioFileName("");
    setEditFormErrors({});
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSong(null);
    setEditCoverImagePreview(null);
    setEditAudioFileName("");
    setEditFormErrors({});
  };

  const playSong = (song) => {
    if (song.fileUrl) {
      window.open(song.fileUrl, "_blank");
    }
  };

  // Helper function to format duration
  const formatDuration = (duration) => {
    if (!duration) return "0:00";
    if (typeof duration === "number") {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
    return duration;
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return "badge bg-success";
      case "pending":
        return "badge bg-warning text-dark";
      case "rejected":
        return "badge bg-danger";
      default:
        return "badge bg-secondary";
    }
  };

  const pendingSongs = songs.filter((s) => s.isApproved === "pending");

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__items">
              <Link to="/artist/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">Songs</li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page d-flex justify-content-between align-items-center">
            <h2 className="card-title text-capitalize mt-3">Songs</h2>
            <button
              className="btn btn-primary"
              onClick={openModal}
              disabled={uploading}
            >
              Add Song
            </button>
          </div>
        </div>

        {/* Search Box */}
        <div className="col-12 mt-3">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search songs by title, artist..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 d-flex align-items-center">
                  <span className="text-muted">
                    Showing {songs.length} of {totalRecords} songs
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All Songs */}
        <div className="col-12 mt-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title">All Songs ({totalRecords})</h5>
              <div className="text-muted">
                Page {currentPage} of {totalPages}
              </div>
            </div>
            <div className="card-body">
              {loadingSongs ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Image</th>
                          <th>Song Name</th>
                          {/* <th>Artist</th> */}
                          <th>Release Date</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Plays</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {songs.map((song) => (
                          <tr key={song._id}>
                            <td>
                              <img
                                src={song.coverImageUrl}
                                alt={song.title}
                                className="rounded"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/50";
                                }}
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span>{song.title.length > 50 ? song.title.substring(0,50)+"..." : song.title}</span>
                                <button
                                  className="btn btn-link btn-sm text-primary ms-2"
                                  onClick={() => playSong(song)}
                                  title="Play song"
                                >
                                  <i className="fa-solid fa-play"></i>
                                </button>
                              </div>
                            </td>
                            {/* <td>{song.artist}</td> */}
                            <td>
                              {song.releaseDate
                                ? new Date(
                                    song.releaseDate,
                                  ).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td>{formatDuration(song.duration)}</td>
                            <td>
                              <span className={getStatusBadge(song.isApproved)}>
                                {song.isApproved}
                              </span>
                            </td>
                            <td>{(song.playCount || 0).toLocaleString()}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-2"
                                onClick={() => openEditModal(song)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteSong(song._id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                        {songs.length === 0 && (
                          <tr>
                            <td
                              colSpan="8"
                              className="text-center text-muted py-4"
                            >
                              No songs found.{" "}
                              {searchTerm
                                ? "Try a different search."
                                : 'Click "Add Song" to create one.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Showing {songs.length} of {totalRecords} songs
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            fetchSongs(currentPage - 1, searchTerm)
                          }
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            fetchSongs(currentPage + 1, searchTerm)
                          }
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Song Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "800px", maxHeight: "90vh", overflowY: "auto" }}
          >
            <button className="modal__close" onClick={closeModal}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">Add New Song</h3>

            <form onSubmit={handleSubmit}>
              <div className="sign__group sign__group--row">
                {/* Basic Information */}
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="title">
                      Song Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control `}
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter song name"
                    />
                    {formErrors.title && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          display: "block",
                          marginTop: "5px",
                        }}
                      >
                        {formErrors.title}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="alternativeTitle">
                      Alternative Title
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="alternativeTitle"
                      name="alternativeTitle"
                      value={formData.alternativeTitle}
                      onChange={handleInputChange}
                      placeholder="Alternative title for the song"
                    />
                  </div>
                </div>

                {/* <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="artist">Artist *</label>
                    <input 
                      type="text"
                      className={`form-control ${!formData.artist?.trim() ? 'is-invalid' : ''}`}
                      id="artist"
                      name="artist"
                      value={formData.artist}
                      onChange={handleInputChange}
                      placeholder="Enter artist name"
                      
                    />
                    {(!formData.artist?.trim() || formErrors.artist) && (
                      <span className="error-text" style={{ color: '#dc3545', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                        {formErrors.artist || 'Artist name is required'}
                      </span>
                    )}
                  </div>
                </div> */}

                <div className="col-lg-6">
                  <div className="form-group">
                    <label
                      className="form-label"
                      htmlFor="recordingVersionTitle"
                    >
                      Recording Version Title *
                    </label>

                    <select
                      className="form-control"
                      id="recordingVersionTitle"
                      name="recordingVersionTitle"
                      value={formData.recordingVersionTitle}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Recording Version</option>
                      <option value="clean">Clean</option>
                      <option value="radio edit">Radio Edit</option>
                      <option value="explicit">Explicit</option>
                    </select>
                    {formErrors.recordingVersionTitle && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          display: "block",
                          marginTop: "5px",
                        }}
                      >
                        {formErrors.recordingVersionTitle}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label
                      className="form-label"
                      htmlFor="recordingRadioProducer"
                    >
                      Recording/Radio Producer
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="recordingRadioProducer"
                      name="recordingRadioProducer"
                      value={formData.recordingRadioProducer}
                      onChange={handleInputChange}
                      placeholder="Producer name"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="releaseDate">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="releaseDate"
                      name="releaseDate"
                      value={formData.releaseDate}
                      onChange={handleInputChange}
                    />
                    {formErrors.releaseDate && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          display: "block",
                          marginTop: "5px",
                        }}
                      >
                        {formErrors.releaseDate}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="themes">
                      Theme *
                    </label>
                    <select
                      className="form-control"
                      id="themes"
                      name="themes"
                      value={formData.themes}
                      onChange={handleInputChange}
                    >
                      <option value="">Select theme</option>
                      {themes.map((theme) => (
                        <option key={theme._id} value={theme._id}>
                          {theme.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.themes && (
                      <span
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          display: "block",
                          marginTop: "5px",
                        }}
                      >
                        {formErrors.themes}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="catalogNumber">
                      Catalog Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="catalogNumber"
                      name="catalogNumber"
                      value={formData.catalogNumber}
                      onChange={handleInputChange}
                      placeholder="Catalog number"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="LabelName">
                      Label Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="LabelName"
                      name="LabelName"
                      value={formData.LabelName}
                      onChange={handleInputChange}
                      placeholder="Record label"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="writers">
                      Writers
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="writers"
                      name="writers"
                      value={formData.writers}
                      onChange={handleInputChange}
                      placeholder="Song writers/composers"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="writersIPICode">
                      Writers IPI Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="writersIPICode"
                      name="writersIPICode"
                      value={formData.writersIPICode}
                      onChange={handleInputChange}
                      placeholder="IPI code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="iswc">
                      ISWC
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="iswc"
                      name="iswc"
                      value={formData.iswc}
                      onChange={handleInputChange}
                      placeholder="International Standard Musical Work Code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="mlcSongCode">
                      MLC Song Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="mlcSongCode"
                      name="mlcSongCode"
                      value={formData.mlcSongCode}
                      onChange={handleInputChange}
                      placeholder="Mechanical Licensing Collective code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="songPublisher">
                      Song Publisher
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="songPublisher"
                      name="songPublisher"
                      value={formData.songPublisher}
                      onChange={handleInputChange}
                      placeholder="Publisher name"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="upcEan">
                      UPC/EAN
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="upcEan"
                      name="upcEan"
                      value={formData.upcEan}
                      onChange={handleInputChange}
                      placeholder="Universal Product Code / European Article Number"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="isrc">
                      ISRC
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="isrc"
                      name="isrc"
                      value={formData.isrc}
                      onChange={handleInputChange}
                      placeholder="International Standard Recording Code"
                    />
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">
                      Description *
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter song description"
                    />
                  </div>
                  {formErrors.description && (
                    <span
                      style={{
                        color: "#dc3545",
                        fontSize: "12px",
                        display: "block",
                      }}
                    >
                      {formErrors.description}
                    </span>
                  )}
                </div>

                {/* File Uploads */}
                <div style={{ display: "flex", gap: "20px" }}>
                  {/* 1. Cover Image Upload */}
                  <div className="col-lg-6">
                    <div className="upload__box">
                      <div className="upload__btn-box">
                        <label className="upload__btn">
                          <p>
                            <i className="fa-solid fa-upload" /> Upload Cover
                            Image *
                          </p>
                          <input
                            type="file"
                            id="coverImage"
                            name="coverImage"
                            className="upload__inputfile"
                            onChange={handleInputChange}
                            accept="image/*"
                          />
                        </label>
                      </div>

                      {/* Preview Section */}
                      {coverImagePreview ? (
                        <div className="file-preview mt-3">
                          <img
                            src={coverImagePreview}
                            alt="Cover preview"
                            className="img-thumbnail"
                            style={{ maxWidth: "200px", maxHeight: "200px" }}
                          />
                          <div
                            className="mt-2 text-success"
                            style={{ fontSize: "12px" }}
                          >
                            <i className="fa-solid fa-check"></i> Image selected
                          </div>
                        </div>
                      ) : (
                        <div className="file-info mt-2">
                          <small className="text-muted">
                            No image selected
                          </small>
                        </div>
                      )}

                      {/* Error Message - Strictly based on formErrors */}
                      {formErrors.coverImage && (
                        <span
                          className="error-text"
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            display: "block",
                            marginTop: "5px",
                          }}
                        >
                          {formErrors.coverImage}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* 2. Audio File Upload */}
                  <div className="col-lg-6">
                    <div className="upload__box">
                      <div className="upload__btn-box">
                        <label className="upload__btn">
                          <p>
                            <i className="fa-solid fa-upload" /> Upload Audio
                            File *
                          </p>
                          <input
                            type="file"
                            id="audioFile"
                            name="audioFile"
                            className="upload__inputfile"
                            onChange={handleInputChange}
                            accept="audio/*"
                          />
                        </label>
                      </div>

                      {/* Preview Section */}
                      {audioFileName ? (
                        <div className="file-preview mt-3">
                          <div
                            className="alert alert-success py-2"
                            style={{ fontSize: "12px" }}
                          >
                            <i className="fa-solid fa-file-audio me-2"></i>
                            {audioFileName}
                          </div>
                        </div>
                      ) : (
                        <div className="file-info mt-2">
                          <small className="text-muted">
                            No audio file selected
                          </small>
                        </div>
                      )}

                      {/* Error Message - Strictly based on formErrors */}
                      {formErrors.audioFile && (
                        <span
                          className="error-text"
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            display: "block",
                            marginTop: "5px",
                          }}
                        >
                          {formErrors.audioFile}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-12">
                  {/* <button
                    type="submit"
                    className="release__buy"
                    disabled={
                      submitting ||
                      !formData.title?.trim() ||
                      // !formData.artist?.trim() ||
                      !formData.releaseDate ||
                      !formData.themes ||
                      !formData.coverImage ||
                      !formData.audioFile ||
                      !!formErrors.coverImage ||
                      !!formErrors.audioFile
                    }
                  >
                    {submitting ? "Adding Song..." : "Add Song"}
                  </button> */}
                  <button
                    type="submit"
                    className="release__buy"
                    disabled={submitting}
                  >
                    {submitting ? "Adding Song..." : "Add Song"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Song Modal */}
      {isEditModalOpen && editingSong && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "800px", maxHeight: "90vh", overflowY: "auto" }}
          >
            <button className="modal__close" onClick={closeEditModal}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">Edit Song</h3>

            <form onSubmit={handleEditSubmit}>
              <div className="sign__group sign__group--row">
                {/* Basic Information */}
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label">Song Name *</label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.title ? "is-invalid" : ""}`}
                      name="title"
                      value={editFormData.title}
                      onChange={handleEditInputChange}
                    />
                    {editFormErrors.title && (
                      <small className="text-danger">
                        {editFormErrors.title}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label
                      className="form-label"
                      htmlFor="editAlternativeTitle"
                    >
                      Alternative Title
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editAlternativeTitle"
                      name="alternativeTitle"
                      value={editFormData.alternativeTitle}
                      onChange={handleEditInputChange}
                      placeholder="Alternative title for the song"
                    />
                  </div>
                </div>

                {/* <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editArtist">Artist *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="editArtist"
                      name="artist"
                      value={editFormData.artist}
                      onChange={handleEditInputChange}
                      placeholder="Enter artist name"
                      required
                    />
                  </div>
                </div> */}

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label">
                      Recording Version Title *
                    </label>
                    <select
                      className={`form-control ${editFormErrors.recordingVersionTitle ? "is-invalid" : ""}`}
                      name="recordingVersionTitle"
                      value={editFormData.recordingVersionTitle || ""}
                      onChange={handleEditInputChange}
                    >
                      <option value="">Select Version</option>
                      <option value="clean">Clean</option>
                      <option value="radio edit">Radio Edit</option>
                      <option value="explicit">Explicit</option>
                    </select>
                    {editFormErrors.recordingVersionTitle && (
                      <small className="text-danger">
                        {editFormErrors.recordingVersionTitle}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label
                      className="form-label"
                      htmlFor="editRecordingRadioProducer"
                    >
                      Recording/Radio Producer
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editRecordingRadioProducer"
                      name="recordingRadioProducer"
                      value={editFormData.recordingRadioProducer}
                      onChange={handleEditInputChange}
                      placeholder="Producer name"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label">Release Date *</label>
                    <input
                      type="date"
                      className={`form-control ${editFormErrors.releaseDate ? "is-invalid" : ""}`}
                      name="releaseDate"
                      value={editFormData.releaseDate}
                      onChange={handleEditInputChange}
                    />
                    {editFormErrors.releaseDate && (
                      <small className="text-danger">
                        {editFormErrors.releaseDate}
                      </small>
                    )}
                  </div>
                </div>

                {/* Theme */}
                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label">Theme *</label>
                    <select
                      className={`form-control ${editFormErrors.themes ? "is-invalid" : ""}`}
                      name="themes"
                      value={editFormData.themes}
                      onChange={handleEditInputChange}
                    >
                      <option value="">Select theme</option>
                      {themes.map((theme) => (
                        <option key={theme._id} value={theme._id}>
                          {theme.name}
                        </option>
                      ))}
                    </select>
                    {editFormErrors.themes && (
                      <small className="text-danger">
                        {editFormErrors.themes}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editCatalogNumber">
                      Catalog Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editCatalogNumber"
                      name="catalogNumber"
                      value={editFormData.catalogNumber}
                      onChange={handleEditInputChange}
                      placeholder="Catalog number"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editLabelName">
                      Label Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editLabelName"
                      name="LabelName"
                      value={editFormData.LabelName}
                      onChange={handleEditInputChange}
                      placeholder="Record label"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editWriters">
                      Writers
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editWriters"
                      name="writers"
                      value={editFormData.writers}
                      onChange={handleEditInputChange}
                      placeholder="Song writers/composers"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editWritersIPICode">
                      Writers IPI Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editWritersIPICode"
                      name="writersIPICode"
                      value={editFormData.writersIPICode}
                      onChange={handleEditInputChange}
                      placeholder="IPI code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editIswc">
                      ISWC
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editIswc"
                      name="iswc"
                      value={editFormData.iswc}
                      onChange={handleEditInputChange}
                      placeholder="International Standard Musical Work Code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editMlcSongCode">
                      MLC Song Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editMlcSongCode"
                      name="mlcSongCode"
                      value={editFormData.mlcSongCode}
                      onChange={handleEditInputChange}
                      placeholder="Mechanical Licensing Collective code"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editSongPublisher">
                      Song Publisher
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editSongPublisher"
                      name="songPublisher"
                      value={editFormData.songPublisher}
                      onChange={handleEditInputChange}
                      placeholder="Publisher name"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editUpcEan">
                      UPC/EAN
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editUpcEan"
                      name="upcEan"
                      value={editFormData.upcEan}
                      onChange={handleEditInputChange}
                      placeholder="Universal Product Code / European Article Number"
                    />
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editIsrc">
                      ISRC
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="editIsrc"
                      name="isrc"
                      value={editFormData.isrc}
                      onChange={handleEditInputChange}
                      placeholder="International Standard Recording Code"
                    />
                  </div>
                </div>

                <div className="col-lg-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editDescription">
                      Description *
                    </label>
                    <textarea
                      className="form-control"
                      id="editDescription"
                      name="description"
                      rows={3}
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Enter song description"
                    />
                  </div>
                  {editFormErrors.description && (
                    <small className="text-danger">
                      {editFormErrors.description}
                    </small>
                  )}
                </div>

                {/* File Uploads */}
                <div style={{ display: "flex" }}>
                  <div className="col-lg-6">
                    <div className="upload__box">
                      <div className="upload__btn-box">
                        <label className="upload__btn">
                          <p>
                            <i className="fa-solid fa-upload" /> Update Cover
                            Image *
                          </p>
                          <input
                            type="file"
                            id="editCoverImage"
                            name="coverImage"
                            className="upload__inputfile"
                            onChange={handleEditInputChange}
                            accept="image/*"
                          />
                        </label>
                      </div>
                      {!editCoverImagePreview && (
                        <div className="file-info mt-2">
                          <small className="text-muted">
                            No image selected
                          </small>
                          {editFormErrors.coverImage && (
                            <span className="error-text d-block mt-1">
                              {editFormErrors.coverImage}
                            </span>
                          )}
                        </div>
                      )}
                      {editCoverImagePreview && (
                        <div className="file-preview mt-3">
                          <img
                            src={editCoverImagePreview}
                            alt="Cover preview"
                            className="img-thumbnail"
                            style={{ maxWidth: "200px", maxHeight: "200px" }}
                          />
                          <div className="mt-2">
                            <small className="text-success">
                              <i className="fa-solid fa-check"></i>{" "}
                              {editFormData.coverImage
                                ? "New image selected"
                                : "Current image"}
                            </small>
                          </div>
                          {editFormErrors.coverImage && (
                            <span className="error-text d-block mt-1">
                              {editFormErrors.coverImage}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="upload__box">
                      <div className="upload__btn-box">
                        <label className="upload__btn">
                          <p>
                            <i className="fa-solid fa-upload" /> Update Audio
                            File *
                          </p>
                          <input
                            type="file"
                            id="editAudioFile"
                            name="audioFile"
                            className="upload__inputfile"
                            onChange={handleEditInputChange}
                            accept="audio/*"
                          />
                        </label>
                      </div>
                      {editAudioFileName && (
                        <div className="file-preview mt-3">
                          <div className="alert alert-success py-2">
                            <i className="fa-solid fa-file-audio me-2"></i>
                            {editAudioFileName}
                          </div>
                          <div className="mt-2">
                            <small className="text-success">
                              <i className="fa-solid fa-check"></i> New audio
                              file selected
                            </small>
                          </div>
                        </div>
                      )}
                      {!editAudioFileName && (
                        <div className="file-info mt-2">
                          {editingSong?.fileUrl ? (
                            <>
                              <small className="text-success d-block">
                                <i className="fa-solid fa-file-audio me-2"></i>{" "}
                                Current file
                              </small>
                              <audio
                                src={editingSong.fileUrl}
                                controls
                                className="w-100 mt-2"
                                style={{ maxHeight: "40px" }}
                                preload="metadata"
                              >
                                Your browser does not support the audio element.
                              </audio>
                              <small className="text-muted d-block mt-1">
                                Upload a new file above to replace
                              </small>
                            </>
                          ) : (
                            <small className="text-muted">
                              No new audio file selected (current file will be
                              kept)
                            </small>
                          )}
                          {editFormErrors.audioFile && (
                            <span className="error-text d-block mt-1">
                              {editFormErrors.audioFile}
                            </span>
                          )}
                        </div>
                      )}
                      {editAudioFileName && editFormErrors.audioFile && (
                        <span className="error-text d-block mt-1">
                          {editFormErrors.audioFile}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-lg-12">
                  <button
                    type="submit"
                    className="release__buy"
                    disabled={
                      submitting ||
                      !!editFormErrors.coverImage ||
                      !!editFormErrors.audioFile
                    }
                  >
                    {submitting ? "Updating..." : "Update Song"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: #fff;
          border-radius: 12px;
          padding: 30px;
          position: relative;
          width: 100%;
        }

        .modal__close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal__close svg {
          fill: #666;
          transition: fill 0.3s ease;
        }

        .modal__close:hover svg {
          fill: #000;
        }

        .sign__title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 25px;
          color: #2f2f2f;
        }

        .sign__group--row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -15px;
        }

        .form-group {
          margin-bottom: 20px;
          width: 100%;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #2f2f2f;
        }

        .form-control {
          width: 100%;
          padding: 12px 15px;
          border: 1px solid #e1e1e1;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
          outline: none;
        }

        .error-text {
          color: #dc3545;
          font-size: 12px;
          margin-top: 5px;
          display: block;
        }

        .form-text {
          font-size: 12px;
          color: #6c757d;
          margin-top: 5px;
        }

        .upload__box {
          margin: 20px 0;
          width: 100%;
        }

        .upload__btn-box {
          margin-bottom: 10px;
        }

        .upload__btn {
          display: inline-block;
          padding: 12px 25px;
          background: #f8f9fa;
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          width: 100%;
        }

        .upload__btn:hover {
          border-color: #007bff;
          background: #f0f8ff;
        }

        .upload__btn p {
          margin: 0;
          color: #6c757d;
        }

        .upload__inputfile {
          display: none;
        }

        .file-preview {
          text-align: center;
        }

        .file-info {
          text-align: center;
        }

        .release__buy {
          width: 100%;
          padding: 12px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .release__buy:hover:not(:disabled) {
          background: #0056b3;
        }

        .release__buy:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }

        .zoom-anim-dialog {
          animation: zoomIn 0.3s ease;
        }

        .badge {
          font-size: 12px;
          padding: 4px 8px;
        }

        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default Songs;
